import { useEffect, useMemo, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { SplitPane, type SplitPaneSize, Toaster, TooltipProvider } from "@attmos/flowstack-ui";
import { RubyPlatformProvider } from "@attmos/ruby-ui";

import { useSession } from "@/api/auth";
import { Footer } from "@/components/layout/Footer";
import { Sidebar } from "@/components/layout/Sidebar";
import { SkipToContent } from "@/components/layout/SkipToContent";
import { TerminalDock, useTerminalDock } from "@/components/layout/TerminalDock";
import { RunCommandPalette } from "@/components/RunCommandPalette";
import { ErrorBoundary } from "@/components/layout/ErrorBoundary";
import { RubyDockedPanel } from "@/components/ruby/RubyDockedPanel";
import { cn } from "@/lib/utils";
import { createFlowStackAdapter } from "@/ruby/adapter";
import {
  RUBY_PANEL_DEFAULT_PX,
  RUBY_PANEL_MAX_PX,
  RUBY_PANEL_MIN_PX,
  useRubyPanelStore,
} from "@/stores/ruby-panel";
import { DOCK_MAX_PERCENT, DOCK_MIN_PERCENT, useTerminalDockStore } from "@/stores/terminal-dock";

/**
 * App shell — a full-height left rail with the workspace column
 * (content outlet + © line) beside it.
 *
 * **The outer axis is a ROW** (flowstack#953). It used to be a column
 * — Header, then a row of [Sidebar, main], then Footer — which boxed
 * the rail between two edge-to-edge bars. Now the rail is the first
 * thing on the page and spans the viewport top to bottom.
 *
 * **And there is no top bar at all** (flowstack#1002). The rail is the
 * app's only chrome: its three occupants — the lockup, the collapse
 * toggle and the ⌘K trigger — moved into the rail's own top and bottom
 * zones, so ``<main>`` now starts at y = 0 and every page gets the
 * 56px back. Page-specific verbs belong to the page's own top-right
 * row, not to global chrome; nothing here blocks one.
 *
 * Wires the global cmd-K palette + sonner toaster + tooltip provider
 * so every route gets them for free.
 *
 * **A route change swaps the page at once — there is no route
 * transition** (fs#1868). It used to be a 120ms `AnimatePresence`
 * crossfade in `mode="wait"`, which holds the new page back until the
 * old one's exit fade reports finished. Two ways that went wrong:
 *
 * - With a live `<Outlet />` in the leaving wrapper, the NEW page
 *   mounted there during the fade, was thrown away, and mounted again
 *   after it — so a ligand picked on Submit Flow within the fade
 *   vanished (fs#1801).
 * - #1858 fixed that by capturing the page with `useOutlet()`, which
 *   made the leaving wrapper keep the OLD page. That is right while the
 *   fade runs, but it made the page swap depend on the animation clock:
 *   in a tab whose animations do not advance (backgrounded, throttled,
 *   or driven by an automation browser) the exit never finishes, and
 *   the URL changed while the old page stayed on screen for good —
 *   clicking a Submit chooser card showed the chooser forever.
 *
 * Nothing that decides WHICH page is on screen may wait on an
 * animation. The wrapper stays keyed by pathname, so a new path is
 * still a fresh mount, exactly as before; `submit_chooser.spec.ts`
 * pins the swap with the browser's animations frozen.
 */
export function AppShell(): JSX.Element {
  const location = useLocation();
  const { data: session } = useSession();
  const adapter = useMemo(() => createFlowStackAdapter(session ?? null), [session]);
  const dock = useTerminalDock();
  const maximized = useTerminalDockStore((s) => s.maximized);
  const dockPercent = useTerminalDockStore((s) => s.heightPercent);
  const setDockPercent = useTerminalDockStore((s) => s.setHeightPercent);
  // The split sizes the PAGE (its primary pane); the dock is the rest.
  const pageSize: SplitPaneSize = !dock.open ? "100%" : maximized ? "0%" : `${100 - dockPercent}%`;
  // A maximized dock leaves the page mounted at 0% (so nothing on it is lost)
  // but out of reach: `inert` takes its controls out of the tab order and the
  // accessibility tree. Set through a ref because React 18 has no `inert` prop.
  const pageHidden = dock.open && maximized;
  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const main = mainRef.current;
    if (main === null) return;
    if (pageHidden) main.setAttribute("inert", "");
    else main.removeAttribute("inert");
  }, [pageHidden]);
  const rubyOpen = useRubyPanelStore((s) => s.open);
  const rubyWidth = useRubyPanelStore((s) => s.widthPx);
  const setRubyWidth = useRubyPanelStore((s) => s.setWidthPx);

  return (
    <RubyPlatformProvider adapter={adapter}>
      <TooltipProvider delayDuration={200}>
        <div className="flex h-screen w-screen text-foreground">
          {/* FIRST FOCUSABLE ELEMENT IN THE DOCUMENT, and it has to stay
              that way (flowstack#983): a skip link placed after the rail
              is a skip link the user tabs 13 stops to reach. It is
              `position: fixed`, so being a child of this row costs the row
              nothing in either state. */}
          <SkipToContent />
          {/* Top to bottom, and FIRST — the rail is a child of the row,
              so it stretches to the full ``h-screen`` height rather
              than to whatever is left between two bars. */}
          <Sidebar />
          {/* The docked Ruby panel beside the rail (fs#2534), split from
              the workspace column by a divider the user drags or moves
              with the arrow keys.

              Always mounted, open panel or not, for the same reason as the
              terminal dock's split below: rendering the column bare while
              the panel is closed and inside the split while it is open
              would make opening Ruby a different element tree, and React
              would remount the page under the user. Closed, the panel's
              pane and the divider are `display: none` and the column takes
              the whole row. */}
          <SplitPane
            orientation="horizontal"
            label="Resize the Ruby panel"
            className={cn(
              "min-w-0 flex-1",
              !rubyOpen && "[&>*:first-child]:hidden [&>[role=separator]]:hidden"
            )}
            size={rubyWidth}
            defaultSize={RUBY_PANEL_DEFAULT_PX}
            minSize={RUBY_PANEL_MIN_PX}
            maxSize={RUBY_PANEL_MAX_PX}
            onSizeChange={(size) => setRubyWidth(Number.parseFloat(String(size)))}
            primary={
              rubyOpen ? (
                <ErrorBoundary>
                  <RubyDockedPanel currentPath={location.pathname} />
                </ErrorBoundary>
              ) : null
            }
            secondary={
              /* The workspace column. ``overflow-hidden`` also resolves
              this column's automatic minimum WIDTH to 0, which is what
              lets a wide table or a DAG canvas inside <main> shrink
              instead of pushing the rail off-screen. Since fs#2534 it is
              the right pane of the Ruby split, so it fills that pane with
              ``h-full`` rather than growing as a row child. */
              <div className="flex h-full min-w-0 flex-col overflow-hidden">
                {/* ``<main>`` is a plain block + overflow-y-auto. An earlier
                draft made IT a flex column so a nested Footer would push
                to the bottom — Rupert PR-334 r2 #1 flagged that as a
                regression risk (flex items have ``min-height: auto``
                which can prevent the scroll container from clamping,
                breaking ``h-full`` tables / split panes / 3D viewer
                canvases). That still holds, and it now has live
                dependants: the run workspace's split panel (#956) and
                the molecule viewer (#946) both need this clamp.

                #953 moved <main> from a row flex item to a COLUMN one,
                which is the axis where ``min-height: auto`` actually
                bites, so the clamp is now spelled out with ``min-h-0``
                rather than left to the (correct but implicit) rule that
                a non-``visible`` overflow already zeroes it. <main>
                itself stays ``display: block`` — the constraint above
                is about <main>'s OWN display, and the Footer reaches the
                bottom as this column's last child, not by making <main>
                a flex parent.

                Since fs#2533 <main> sits in the top pane of the terminal
                dock's split (below), so it fills that pane with ``h-full``
                instead of growing as a column child with ``flex-1``; the
                ``min-h-0`` clamp and the block display are unchanged.

                `tabIndex={-1}` is what makes the skip link work rather
                than merely exist (#983). Without it `main.focus()` is a
                no-op, the next Tab resumes from the rail, and the link
                moves the scroll position while a screen reader is still
                sitting in the chrome. `-1` keeps it OUT of the tab order
                — it is a landmark, not a stop. */}
                {/* The page over the terminal dock (fs#2533), split by a
                divider the user drags or moves with the arrow keys.

                The split is ALWAYS mounted, open dock or not, and that is
                load-bearing: rendering <main> bare while the dock is closed
                and inside the split while it is open would make opening the
                dock a different element tree, so React would remount the
                page — and the Workbench's "Open in dock" would throw away
                the very edits on screen. Closed, the page takes 100% and the
                divider and the empty bottom pane are `display: none`. */}
                <SplitPane
                  orientation="vertical"
                  label="Resize the terminal dock"
                  className={cn(
                    "min-h-0 flex-1",
                    !dock.open && "[&>*:last-child]:hidden [&>[role=separator]]:hidden"
                  )}
                  size={pageSize}
                  minSize={dock.open && !maximized ? `${100 - DOCK_MAX_PERCENT}%` : 0}
                  maxSize={dock.open && !maximized ? `${100 - DOCK_MIN_PERCENT}%` : "100%"}
                  onSizeChange={(size) => setDockPercent(100 - Number.parseFloat(String(size)))}
                  primary={
                    <main
                      ref={mainRef}
                      className="h-full min-h-0 overflow-y-auto"
                      id="main"
                      tabIndex={-1}
                    >
                      <ErrorBoundary>
                        <div
                          key={location.pathname}
                          /* The page gutter, and the app's only one. ``p-gutter``
                         resolves to ``--gutter``. It sits INSIDE <main>, on
                         the page wrapper: <main>'s own box is what
                         ``app_frame.spec.ts`` measures against the rail, and
                         padding here cannot reach it.

                         The bottom is the plain gutter too (fs#2540). It used
                         to add ``--assistant-clearance`` to keep the page's last
                         row out from under ruby-ui's fixed floating widget;
                         Ruby now lives only in the docked panel, so nothing is
                         fixed over the page and the extra band is gone. */
                          className="p-gutter"
                        >
                          <Outlet />
                        </div>
                      </ErrorBoundary>
                    </main>
                  }
                  secondary={
                    dock.open ? (
                      <ErrorBoundary>
                        <TerminalDock />
                      </ErrorBoundary>
                    ) : null
                  }
                />
                {/* Footer sits OUTSIDE <main> so the browser exposes it as
                the page-level ``contentinfo`` landmark (a <footer>
                nested in <main> is scoped to that main and never
                reaches ``contentinfo``). Rupert PR-334 r2 #2.

                It moved INSIDE the workspace column in #953 so it stops
                spanning under the rail — and that keeps the landmark,
                because ``<footer>`` only loses ``contentinfo`` when it
                descends from a sectioning element (article / aside /
                main / nav / section). Everything between it and <body>
                here is a plain <div>, and it is still a SIBLING of
                <main>. `AppShell.test.tsx` pins both halves. */}
                <Footer />
              </div>
            }
          />
          {/* Overlays, not layout: the palette portals when open and
              renders nothing when closed, and the toaster's list is
              ``position: fixed``. Neither is an in-flow child of the row
              above, so neither takes width from the workspace.

              There is deliberately no ruby-ui ``RubyWidget`` here
              (fs#2540): the docked panel above is the only Ruby surface,
              opened from the rail's Ruby row. ``RubyPlatformProvider``
              stays because the panel reads its adapter. */}
          <RunCommandPalette />
          <Toaster />
        </div>
      </TooltipProvider>
    </RubyPlatformProvider>
  );
}
